import { registerTokenStore, type TokenStore } from '@shared/api/token-store';

export type WebScope = 'client' | 'associate';

export function getActiveWebScope(): WebScope {
  if (typeof window === 'undefined') return 'client';
  const path = window.location.pathname;
  const search = window.location.search;
  if (path.startsWith('/associate') || search.includes('as=associate')) {
    return 'associate';
  }
  return 'client';
}

function getAccessKey(scope?: string): string {
  const s = scope === 'associate' || scope === 'client' ? scope : getActiveWebScope();
  return `ecregi_${s}.accessToken`;
}

function get(k: string): string | null {
  try {
    return sessionStorage.getItem(k);
  } catch {
    return null;
  }
}

function set(k: string, v: string | null): void {
  try {
    if (v) sessionStorage.setItem(k, v);
    else sessionStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

// Clean up legacy localStorage if present from prior builds
function purgeLegacyLocalStorage() {
  try {
    localStorage.removeItem('ecregi.accessToken');
    localStorage.removeItem('ecregi.refreshToken');
  } catch {
    /* ignore */
  }
}

const store: TokenStore = {
  getAccessToken: (scope?: string) => {
    const raw = get(getAccessKey(scope));
    if (raw) return raw;
    const s = scope === 'associate' || scope === 'client' ? scope : getActiveWebScope();
    return get(`ecregi_${s}.session`) ? 'cookie-session' : null;
  },
  setAccessToken: (t?: string | null, scope?: string) => set(getAccessKey(scope), t || 'cookie-session'),
  getRefreshToken: () => null,
  setRefreshToken: () => {
    // No-op: refresh tokens are handled securely via HttpOnly cookies
  },
  clear: (scope?: string) => {
    const s = scope === 'associate' || scope === 'client' ? scope : getActiveWebScope();
    set(getAccessKey(scope), null);
    try {
      sessionStorage.removeItem(`ecregi_${s}.session`);
    } catch {
      /* ignore */
    }
    purgeLegacyLocalStorage();
  },
};

export function installWebTokenStore() {
  purgeLegacyLocalStorage();
  registerTokenStore(store);
}
